// transport.ts is the remote Backend implementation — every call lands on
// /_backlit/... on the same origin. The SDK runs on the glow's subdomain
// and the data API is reached via path-routing on that same origin
// (locally: cmd/edge proxies /_backlit/... to cmd/data-api; prod: GCP LB
// does path matching). Cookies attach automatically via credentials:
// "same-origin"; we never read or set them ourselves.
//
// All envelope decoding, header handling, and error mapping live in the
// internal request() function. The exported `remoteBackend` is a thin
// adapter that wires the namespace operations onto request().

import { BacklitError, type BacklitErrorCode } from "./errors.js";
import { SERVER_CODES, type ServerErrorCode } from "./server-codes.gen.js";
import { PERMISSIONS } from "./permissions.gen.js";
import { assertOnBacklitHost } from "./host.js";
import { maybeGzip } from "./body.js";
import type {
  BacklitUser,
  Backend,
  BlobResult,
  ListEntry,
  ListOptions,
  ListPage,
  BatchEntry,
  RecordBatchEntry,
  GetBatchOptions,
  Permission,
  PutResult,
  PutIfMatchResult,
  CaptureCreateResult,
  CaptureListEntry,
  RecordResult,
  RecordListEntry,
  RecordPutIfMatchResult,
  StorageChangeHandler,
  UIConfig,
  Unsubscribe,
  PushSubscribePayload,
  PushUnsubscribePayload,
  SubscribeResult,
} from "./backend.js";
import { remoteOnStorageChange } from "./events.js";
import { now, parseServerDate } from "./offline-identity.js";

// --- response metadata (internal; backlit.offline) --------------------------
// Every data-plane response is reported to the registered observers as a
// ResponseMeta — status plus the identity the server stated for it
// (X-Backlit-UID; "" = no information: an anonymous session or an older
// server). The same meta is attached to the result object (or thrown error)
// the response produced, readable via responseMetaOf(), so the offline layer
// can gate a write-through on the uid of THAT response. Write results carry it
// too — the put results, every CAS outcome (match, 412 witness, thrown error)
// — and deleteWithMeta() resolves it for a delete, while the public delete
// still resolves undefined; the tag lives in a WeakMap, so no result object
// changes shape. A request that gets no answer at all (the fetch rejects:
// BacklitError "network") reaches the failure observers instead, with its
// path. Headers are read tolerantly (a test double may carry none)
// and every observer is isolated: a page that never calls offline.enable()
// registers none, so nothing changes.
export interface ResponseMeta {
  readonly path: string;
  readonly status: number;
  readonly uid: string;
  // owner is X-Backlit-Record-Owner (null when absent).
  readonly owner: string | null;
  readonly date: number;
  // sentAt is now() taken immediately before the fetch (the request start).
  readonly sentAt: number;
  readonly redirected: boolean;
  readonly crcPresent: boolean;
}

const observers = new Set<(m: ResponseMeta) => void>();
const failureObservers = new Set<(path: string) => void>();
const metaByResult = new WeakMap<object, ResponseMeta>();

// addResponseObserver registers fn for every response; returns an unregister.
export function addResponseObserver(fn: (m: ResponseMeta) => void): () => void {
  observers.add(fn);
  return () => {
    observers.delete(fn);
  };
}

// addFailureObserver registers fn for every request that got no answer (the
// fetch rejected: BacklitError "network"); returns an unregister.
export function addFailureObserver(fn: (path: string) => void): () => void {
  failureObservers.add(fn);
  return () => {
    failureObservers.delete(fn);
  };
}

// netError reports a transport failure of path to the failure observers and
// returns the BacklitError("network") the caller throws.
function netError(path: string, cause: unknown): BacklitError {
  for (const fn of Array.from(failureObservers)) {
    try {
      fn(path);
    } catch {
      /* an observer never breaks the request */
    }
  }
  return new BacklitError("network", cause instanceof Error ? cause.message : "fetch failed", 0, cause);
}

// responseMetaOf returns the meta of the response that produced x (a result
// object, a list page, or a thrown BacklitError), if any.
export function responseMetaOf(x: unknown): ResponseMeta | undefined {
  return x !== null && typeof x === "object" ? metaByResult.get(x) : undefined;
}

function header(res: Response, name: string): string | null {
  try {
    return res.headers?.get?.(name) ?? null;
  } catch {
    return null;
  }
}

function observe(res: Response, path: string, sentAt: number): ResponseMeta {
  const meta: ResponseMeta = {
    path,
    status: typeof res.status === "number" ? res.status : 0,
    uid: header(res, "X-Backlit-UID") ?? "",
    owner: header(res, "X-Backlit-Record-Owner"),
    date: parseServerDate(header(res, "Date")),
    sentAt,
    redirected: res.redirected === true,
    crcPresent: (header(res, "X-Backlit-CRC32C") ?? "") !== "",
  };
  for (const fn of Array.from(observers)) {
    try {
      fn(meta);
    } catch {
      /* an observer never breaks the request */
    }
  }
  return meta;
}

// tagMeta attaches meta to an object result (non-objects pass through).
function tagMeta<T>(x: T, meta: ResponseMeta | undefined): T {
  if (meta && x !== null && typeof x === "object") metaByResult.set(x as object, meta);
  return x;
}

// PERMISSIONS (the permission values the data-api may return on
// /_backlit/auth/me; anything outside it decodes to "") is GENERATED from
// internal/firestore.Permissions so it can't drift from the server enum.
// Edit internal/firestore + run `task permissions:sync`.

// SERVER_CODES (the codes a server envelope may carry; anything outside it is
// surfaced as "internal" to keep callers' switch statements closed) is imported
// from server-codes.gen.ts, GENERATED from internal/errcodes.All so the Go
// emitters and this allowlist can never drift. Edit internal/errcodes + run
// `task errcodes:sync` to change it — never edit the generated file by hand.

// AuthMeResponse is the /_backlit/auth/me JSON object shape: the five
// signed-in user fields, each tolerated-missing (string | null | undefined).
// The endpoint returns a bare JSON `null` body (decoded to a JS null) when no
// user is signed in; authMe() handles that null before this shape applies.
interface AuthMeResponse {
  uid?: string | null;
  name?: string | null;
  email?: string | null;
  mobile?: string | null;
  permission?: string | null;
}

// AuthUsersResponse is the /_backlit/auth/users JSON object shape: a
// {users:[...], next} envelope whose entries reuse the AuthMeResponse field
// shape (each field tolerated-missing) and whose `next` is the opaque cursor
// for the following page (mirroring ListResponse). decodeRoster maps each
// entry into a BacklitUser with the same per-field tolerance authMe applies
// and wraps the page in a ListPage<BacklitUser>.
interface AuthUsersResponse {
  users?: AuthMeResponse[];
  next?: unknown;
}

interface RequestOpts {
  method: "GET" | "PUT" | "POST" | "DELETE";
  path: string;
  body?: BodyInit | undefined;
  contentType?: string | undefined;
  contentEncoding?: string | undefined;
  // headers carries extra request headers (e.g. records-reassign's
  // X-Backlit-New-Owner). Merged first; Content-Type/Content-Encoding/Accept
  // set below take precedence. Existing callers omit it.
  headers?: Record<string, string> | undefined;
  expect: "blob" | "json" | "void";
}

// Overloads so callers get a precise return type without explicit generics.
function request(opts: RequestOpts & { expect: "blob" }): Promise<BlobResult>;
function request<T = unknown>(opts: RequestOpts & { expect: "json" }): Promise<T>;
function request(opts: RequestOpts & { expect: "void" }): Promise<void>;
async function request(opts: RequestOpts): Promise<unknown> {
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };
  if (opts.contentType) {
    headers["Content-Type"] = opts.contentType;
  }
  if (opts.contentEncoding) {
    headers["Content-Encoding"] = opts.contentEncoding;
  }
  if (opts.expect === "json") {
    headers["Accept"] = "application/json";
  }

  assertOnBacklitHost();
  const init: RequestInit = { method: opts.method, headers };
  if (opts.body !== undefined) {
    init.body = opts.body;
  }
  const [res, meta] = await fetchObserved(opts.path, init);

  if (!res.ok) {
    throw tagMeta(await decodeError(res), meta);
  }

  if (opts.expect === "void") {
    return undefined;
  }
  if (opts.expect === "blob") {
    // fetch() auto-decodes Content-Encoding: gzip in the browser, so
    // .blob() already holds the uncompressed bytes. We do NOT echo the
    // Content-Encoding header on the BlobResult — callers see a
    // decompressed Blob with the original content-type. The
    // X-Backlit-CRC32C header carries the integrity hash of those
    // uncompressed bytes (mirrors gcs.UncompressedCRC32C server-side).
    const value = await res.blob();
    const contentType = res.headers.get("Content-Type") ?? "application/octet-stream";
    // FE8 — CRC read policy: TOLERATE a missing X-Backlit-CRC32C on GET. The
    // data-api always sets it, but a missing value (legacy object, a proxy
    // that stripped the non-standard header) yields crc32c: "" rather than a
    // thrown error. Reads are about the bytes; the CRC is an optional dedup/
    // integrity aid, so its absence must not fail the read. This is the
    // deliberate asymmetry with PUT, where the CRC is the entire success
    // payload and an empty value IS a protocol violation (see missingCRC32C).
    // NOTE for Slice 7 doc: document this read-tolerates / write-strict split
    // in the SDK reference (§7.3).
    const crc32c = res.headers.get("X-Backlit-CRC32C") ?? "";
    const out: BlobResult = { value, contentType, crc32c };
    return tagMeta(out, meta);
  }
  // expect === "json"
  try {
    return tagMeta(await res.json(), meta);
  } catch (cause) {
    throw new BacklitError(
      "internal",
      "server responded with unparseable JSON",
      res.status,
      cause,
    );
  }
}

// fetchObserved is the fetch step request(), the records GET and the offline
// replay share: cookies ride along (same-origin), a transport failure becomes
// BacklitError("network"), every answer reaches the response observers, and
// the stale-version hint is honored — the data-api sets Backlit-Version-Stale:
// 1 when the session JWT's `version` claim no longer matches the glow's
// live_version, surfaced as the underscored, intentionally-non-public
// window.backlit._versionStale flag so apps that care can show a "reload to
// upgrade" UX (not part of the v1 surface contract; may evolve).
async function fetchObserved(path: string, init: RequestInit): Promise<[Response, ResponseMeta]> {
  let res: Response;
  const sentAt = now();
  try {
    res = await fetch(path, { ...init, credentials: "same-origin" });
  } catch (cause) {
    throw netError(path, cause);
  }
  const meta = observe(res, path, sentAt);
  if (header(res, "Backlit-Version-Stale") === "1") setVersionStaleFlag();
  return [res, meta];
}

// putJSON wraps a write-then-decode flow: gzip-pre-flight, send the
// PUT/POST, decode the {"crc32c": "..."} envelope every successful
// write returns. Shared between data.put, userdata.put,
// capture.update — the capture.create variant adds the handle
// field on top.
async function putJSON(
  method: "PUT" | "POST",
  path: string,
  body: BodyInit,
  contentType: string,
): Promise<{ crc32c?: string; handle?: string }> {
  const compressed = await maybeGzip(body, contentType);
  return request<{ crc32c?: string; handle?: string }>({
    method,
    path,
    body: compressed.body,
    contentType,
    contentEncoding: compressed.encoding ?? undefined,
    expect: "json",
  });
}

// requestPutIfMatch is the dedicated transport for the compare-and-swap
// write surface (data.putIfMatch / userdata.putIfMatch). It mirrors the
// header/gzip handling of request()+putJSON but classifies the response
// purely by HTTP status:
//   - 200 → the write landed; parse and return { matched: true, crc32c }.
//   - 412 → CRC mismatch (lost race); read the current value as a witness
//     exactly like a GET decode and return { matched: false, witness }.
//     This is a NORMAL result and must never throw.
//   - anything else (404 not_found, 403 forbidden, 415, …) → throw via
//     the shared decodeError, same as every other write.
// The expected-CRC sentinel is carried in a wire-internal request header
// that is ALWAYS sent — an empty value means "create-only" (write iff the
// key is currently absent).
async function requestPutIfMatch(
  path: string,
  body: BodyInit,
  contentType: string,
  expectedCrc32c: string,
): Promise<PutIfMatchResult> {
  const compressed = await maybeGzip(body, contentType);

  const headers: Record<string, string> = {
    "Content-Type": contentType,
    // Always sent; empty value ⇒ create-only. Wire-internal detail.
    "X-Backlit-Expected-CRC32C": expectedCrc32c,
  };
  if (compressed.encoding) {
    headers["Content-Encoding"] = compressed.encoding;
  }

  assertOnBacklitHost();
  let res: Response;
  const sentAt = now();
  try {
    res = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers,
      body: compressed.body,
    });
  } catch (cause) {
    throw netError(path, cause);
  }
  const meta = observe(res, path, sentAt);

  // Preserve the stale-version hint, same as request().
  if (res.headers.get("Backlit-Version-Stale") === "1") {
    setVersionStaleFlag();
  }

  if (res.status === 200) {
    let payload: { crc32c?: unknown };
    try {
      payload = await res.json();
    } catch (cause) {
      throw new BacklitError(
        "internal",
        "server responded with unparseable JSON",
        res.status,
        cause,
      );
    }
    if (typeof payload.crc32c !== "string" || payload.crc32c.length === 0) {
      throw missingCRC32C();
    }
    return tagMeta<PutIfMatchResult>({ matched: true, crc32c: payload.crc32c }, meta);
  }

  if (res.status === 412) {
    // The witness is the current stored value, framed exactly like a GET
    // response (fetch() auto-decodes Content-Encoding: gzip). Mirror the
    // FE8 read policy: tolerate a missing X-Backlit-CRC32C → "".
    const value = await res.blob();
    const witnessContentType = res.headers.get("Content-Type") ?? "application/octet-stream";
    const witnessCrc32c = res.headers.get("X-Backlit-CRC32C") ?? "";
    return tagMeta<PutIfMatchResult>(
      {
        matched: false,
        witness: { value, contentType: witnessContentType, crc32c: witnessCrc32c },
      },
      meta,
    );
  }

  throw tagMeta(await decodeError(res), meta);
}

// missingCRC32C is the canonical bad-server-response error: the data-
// api always populates `crc32c` on success, so an empty value is a
// protocol violation worth surfacing rather than silently returning
// "".
function missingCRC32C(): BacklitError {
  return new BacklitError(
    "internal",
    "server response missing crc32c",
    200,
  );
}

// --- records surface ------------------------------------------------------
// The records GET / CAS-witness need the owner header, which the shared
// request() blob path doesn't surface, so the records blob fetch is dedicated
// (over the shared fetchObserved step, plus decodeError).

// recordsBlobResult decodes a records GET / 412-witness Response into a
// RecordResult: the data BlobResult fields plus the owner uid from the
// X-Backlit-Record-Owner header (tolerate-missing → "", the FE8 read policy).
async function recordsBlobResult(res: Response): Promise<RecordResult> {
  const value = await res.blob();
  const contentType = res.headers.get("Content-Type") ?? "application/octet-stream";
  const crc32c = res.headers.get("X-Backlit-CRC32C") ?? "";
  const owner = res.headers.get("X-Backlit-Record-Owner") ?? "";
  return { value, contentType, crc32c, owner };
}

async function recordsGetRequest(path: string): Promise<RecordResult> {
  assertOnBacklitHost();
  const [res, meta] = await fetchObserved(path, { method: "GET" });
  if (!res.ok) {
    throw tagMeta(await decodeError(res), meta);
  }
  return tagMeta(await recordsBlobResult(res), meta);
}

// recordsRequestPutIfMatch mirrors requestPutIfMatch but reads the owner header
// on BOTH the 200 (the committed owner) and the 412 witness (D7).
async function recordsRequestPutIfMatch(
  path: string,
  body: BodyInit,
  contentType: string,
  expectedCrc32c: string,
): Promise<RecordPutIfMatchResult> {
  const compressed = await maybeGzip(body, contentType);

  const headers: Record<string, string> = {
    "Content-Type": contentType,
    "X-Backlit-Expected-CRC32C": expectedCrc32c,
  };
  if (compressed.encoding) {
    headers["Content-Encoding"] = compressed.encoding;
  }

  assertOnBacklitHost();
  let res: Response;
  const sentAt = now();
  try {
    res = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers,
      body: compressed.body,
    });
  } catch (cause) {
    throw netError(path, cause);
  }
  const meta = observe(res, path, sentAt);

  if (res.headers.get("Backlit-Version-Stale") === "1") {
    setVersionStaleFlag();
  }

  if (res.status === 200) {
    let payload: { crc32c?: unknown };
    try {
      payload = await res.json();
    } catch (cause) {
      throw new BacklitError("internal", "server responded with unparseable JSON", res.status, cause);
    }
    if (typeof payload.crc32c !== "string" || payload.crc32c.length === 0) {
      throw missingCRC32C();
    }
    const owner = res.headers.get("X-Backlit-Record-Owner") ?? "";
    return tagMeta<RecordPutIfMatchResult>({ matched: true, crc32c: payload.crc32c, owner }, meta);
  }

  if (res.status === 412) {
    return tagMeta<RecordPutIfMatchResult>({ matched: false, witness: await recordsBlobResult(res) }, meta);
  }

  throw tagMeta(await decodeError(res), meta);
}

// --- offline replay (backlit.offline drain) ----------------------------------
// replayWrite sends one queued write the way the drain replays it: a put with
// no base is a plain PUT; a put with a base (even "") is a CAS POST to the
// store's -cas route; a delete is a DELETE, conditional
// (X-Backlit-Expected-CRC32C) only on a non-empty base. A user op sends
// X-Backlit-Expect-UID, so a session that switched user is refused (409
// identity_mismatch) rather than writing as someone else. It never throws: a
// transport failure is status 0 / code "network". The answer is classified by
// status; X-Backlit-UID, X-Backlit-Record-Owner and the X-Backlit-Witness
// marker read "" / false when absent (an older server). A 412 carries the
// stored value (the witness) on a CAS route — the old-server contract, marker
// or not — and on a DELETE only when marked; any other failure decodes its
// error code.
export interface ReplayWrite {
  store: "data" | "userdata" | "records";
  key: string;
  bytes: ArrayBuffer | null;
  contentType: string;
  base: string | null;
  expectUid: string;
}

export interface ReplayResult {
  status: number;
  code: string;
  crc32c: string;
  owner: string;
  uid: string;
  witnessMarked: boolean;
  witness: RecordResult | null;
}

export async function replayWrite(w: ReplayWrite): Promise<ReplayResult> {
  const put = w.bytes !== null;
  const cas = put && w.base !== null;
  const path = `/_backlit/${w.store}${cas ? "-cas" : ""}/${encodeURIComponent(w.key)}`;
  const headers: Record<string, string> = {};
  const init: RequestInit = { method: !put ? "DELETE" : cas ? "POST" : "PUT", headers };
  if (put) {
    const z = await maybeGzip(w.bytes as ArrayBuffer, w.contentType);
    init.body = z.body;
    headers["Content-Type"] = w.contentType;
    if (z.encoding) headers["Content-Encoding"] = z.encoding;
  }
  if (cas || w.base) headers["X-Backlit-Expected-CRC32C"] = w.base as string;
  if (w.expectUid) headers["X-Backlit-Expect-UID"] = w.expectUid;
  const out: ReplayResult = { status: 0, code: "network", crc32c: "", owner: "", uid: "", witnessMarked: false, witness: null };
  try {
    const [res, meta] = await fetchObserved(path, init);
    out.status = meta.status;
    out.uid = meta.uid;
    out.code = "";
    out.owner = header(res, "X-Backlit-Record-Owner") ?? "";
    out.witnessMarked = header(res, "X-Backlit-Witness") === "1";
    if (res.ok) {
      const c = put ? ((await res.json()) as { crc32c?: unknown } | null)?.crc32c : "";
      out.crc32c = typeof c === "string" ? c : "";
    } else if (out.status === 412 && (put || out.witnessMarked)) {
      out.witness = await recordsBlobResult(res);
    } else {
      out.code = (await decodeError(res)).code;
    }
  } catch {
    /* a transport failure (status 0, "network"), or an unreadable body the
       status already classifies */
  }
  return out;
}

// captureCreateAt stores body at a handle the caller minted — POST
// /_backlit/capture/{handle}, "{prefix}-{uuid v4}" (the offline capture
// opt-in, online and on replay): 201 on a fresh create, 200 when the handle
// already exists (crc32c echoes the request body). An older data-api answers
// 404 / 405, thrown like any failure (the caller reads err.status).
export async function captureCreateAt(handle: string, body: BodyInit, contentType: string): Promise<CaptureCreateResult> {
  const res = await putJSON("POST", `/_backlit/capture/${encodeURIComponent(handle)}`, body, contentType);
  if (typeof res.crc32c !== "string" || res.crc32c.length === 0) {
    throw missingCRC32C();
  }
  return { handle, crc32c: res.crc32c };
}

// deleteWithMeta is the DELETE behind the public xDelete (which drops the
// result): it resolves the 2xx response's meta, and a failure throws the same
// tagged BacklitError. Internal — the offline layer reads the meta.
export async function deleteWithMeta(
  store: "data" | "userdata" | "records",
  key: string,
): Promise<ResponseMeta | undefined> {
  assertOnBacklitHost();
  const [res, meta] = await fetchObserved(`/_backlit/${store}/${encodeURIComponent(key)}`, {
    method: "DELETE",
    headers: {},
  });
  if (!res.ok) {
    throw tagMeta(await decodeError(res), meta);
  }
  return meta;
}

// setVersionStaleFlag flips window.backlit._versionStale = true. Idempotent
// — once set, subsequent calls leave it alone. The flag is read-only from
// app code; the SDK never clears it.
function setVersionStaleFlag(): void {
  // Best-effort: in a non-browser harness (Node tests) window may be
  // undefined; swallow rather than throw.
  if (typeof globalThis === "undefined") return;
  type Stash = { _versionStale?: boolean };
  const g = globalThis as { backlit?: Stash };
  if (!g.backlit) g.backlit = {};
  g.backlit._versionStale = true;
}

// decodeError parses the server's {error:{code,message}} envelope. Non-
// JSON 4xx/5xx (LB error pages, proxies that overwrite the body) collapse
// to code: "internal".
async function decodeError(res: Response): Promise<BacklitError> {
  const status = res.status;
  const ctype = res.headers.get("Content-Type") ?? "";
  if (!ctype.toLowerCase().includes("application/json")) {
    return new BacklitError("internal", `server returned HTTP ${status}`, status);
  }
  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    return new BacklitError("internal", `server returned HTTP ${status} with unparseable body`, status);
  }
  if (
    payload !== null &&
    typeof payload === "object" &&
    "error" in payload &&
    (payload as { error: unknown }).error !== null &&
    typeof (payload as { error: unknown }).error === "object"
  ) {
    const errObj = (payload as { error: { code?: unknown; message?: unknown } }).error;
    const rawCode = typeof errObj.code === "string" ? errObj.code : "";
    const message = typeof errObj.message === "string" && errObj.message.length > 0
      ? errObj.message
      : `HTTP ${status}`;
    const code = (SERVER_CODES.has(rawCode as ServerErrorCode) ? rawCode : "internal") as BacklitErrorCode;
    return new BacklitError(code, message, status);
  }
  return new BacklitError("internal", `server returned HTTP ${status}`, status);
}

// remoteBackend wires the path layout for /_backlit/... onto the Backend
// interface. Path-construction lives here, behind the Backend seam, rather
// than scattered through the namespace modules.
//
// Note on auth.me(): the auth namespace itself swallows everything to
// honour "never throws", but the backend method is allowed to throw — the
// namespace module catches and translates. Keeps the Backend contract
// uniform across operations.
export const remoteBackend: Backend = {
  async authMe(): Promise<BacklitUser | null> {
    // A single GET /_backlit/auth/me round-trip (no memoization — the old
    // paired-call cache is gone with the one-method surface). The endpoint
    // returns a bare JSON `null` body for no-user, or the 5-field object.
    const res = await request<AuthMeResponse | null>({
      method: "GET",
      path: "/_backlit/auth/me",
      expect: "json",
    });
    if (res === null || typeof res !== "object") return null;
    const email = typeof res.email === "string" ? res.email : "";
    const uid = typeof res.uid === "string" ? res.uid : "";
    // Defensive no-user decode (D-b): an object missing BOTH identity anchors
    // is treated as null, same as a bare `null` body.
    if (email === "" && uid === "") return null;
    const permission = PERMISSIONS.has(res.permission as Permission)
      ? (res.permission as Permission)
      : "";
    return {
      uid,
      email,
      name: typeof res.name === "string" ? res.name : "",
      mobile: typeof res.mobile === "string" ? res.mobile : "",
      permission,
    };
  },

  async authUsers(opts?: ListOptions): Promise<ListPage<BacklitUser>> {
    // A single GET /_backlit/auth/users round-trip for one cursor-paginated
    // page. The server is admin-gated, so a non-admin / signed-out / Public
    // caller gets a 4xx that request() → decodeError turns into a thrown
    // BacklitError (forbidden / unauthenticated / invalid_cursor) — NOT
    // swallowed (contrast authMe). The success body is a {users:[...], next}
    // envelope decoded with the authMe per-field tolerance into a
    // ListPage<BacklitUser>.
    const res = await request<AuthUsersResponse>({
      method: "GET",
      path: `/_backlit/auth/users${pageQuery(opts)}`,
      expect: "json",
    });
    return decodeRoster(res);
  },

  uiConfig(): Promise<UIConfig> {
    // Single-flight: concurrent callers (the FAB at load, offline.enable())
    // share one GET /_backlit/ui. A later, sequential call fetches afresh.
    if (uiInflight) return uiInflight;
    const p = (async () => {
      const res = await request<UIConfigResponse | null>({
        method: "GET",
        path: "/_backlit/ui",
        expect: "json",
      });
      const cfg = tagMeta(decodeUIConfig(res), responseMetaOf(res));
      uiMemo = cfg;
      for (const fn of Array.from(uiListeners)) {
        try {
          fn(cfg);
        } catch {
          /* a listener never breaks the bootstrap */
        }
      }
      return cfg;
    })();
    uiInflight = p;
    const settled = () => {
      if (uiInflight === p) uiInflight = null;
    };
    p.then(settled, settled);
    return p;
  },

  dataGet(key: string): Promise<BlobResult> {
    return request({
      method: "GET",
      path: `/_backlit/data/${encodeURIComponent(key)}`,
      expect: "blob",
    });
  },

  async dataPut(key: string, body: BodyInit, contentType: string): Promise<PutResult> {
    const res = await putJSON(
      "PUT",
      `/_backlit/data/${encodeURIComponent(key)}`,
      body,
      contentType,
    );
    if (typeof res.crc32c !== "string" || res.crc32c.length === 0) {
      throw missingCRC32C();
    }
    return tagMeta({ crc32c: res.crc32c }, responseMetaOf(res));
  },

  dataPutIfMatch(
    key: string,
    body: BodyInit,
    contentType: string,
    expectedCrc32c: string,
  ): Promise<PutIfMatchResult> {
    return requestPutIfMatch(
      `/_backlit/data-cas/${encodeURIComponent(key)}`,
      body,
      contentType,
      expectedCrc32c,
    );
  },

  dataDelete(key: string): Promise<void> {
    return deleteWithMeta("data", key).then(() => undefined);
  },

  dataList(prefix: string, opts?: ListOptions): Promise<ListPage<ListEntry>> {
    return listKeys("/_backlit/data", prefix, opts);
  },

  dataGetBatch(prefix: string, opts?: GetBatchOptions): Promise<ListPage<BatchEntry>> {
    return batchEntries("/_backlit/data-batch", prefix, opts, false);
  },

  userdataGet(key: string): Promise<BlobResult> {
    return request({
      method: "GET",
      path: `/_backlit/userdata/${encodeURIComponent(key)}`,
      expect: "blob",
    });
  },

  async userdataPut(key: string, body: BodyInit, contentType: string): Promise<PutResult> {
    const res = await putJSON(
      "PUT",
      `/_backlit/userdata/${encodeURIComponent(key)}`,
      body,
      contentType,
    );
    if (typeof res.crc32c !== "string" || res.crc32c.length === 0) {
      throw missingCRC32C();
    }
    return tagMeta({ crc32c: res.crc32c }, responseMetaOf(res));
  },

  userdataPutIfMatch(
    key: string,
    body: BodyInit,
    contentType: string,
    expectedCrc32c: string,
  ): Promise<PutIfMatchResult> {
    return requestPutIfMatch(
      `/_backlit/userdata-cas/${encodeURIComponent(key)}`,
      body,
      contentType,
      expectedCrc32c,
    );
  },

  userdataDelete(key: string): Promise<void> {
    return deleteWithMeta("userdata", key).then(() => undefined);
  },

  userdataList(prefix: string, opts?: ListOptions): Promise<ListPage<ListEntry>> {
    return listKeys("/_backlit/userdata", prefix, opts);
  },

  userdataGetBatch(prefix: string, opts?: GetBatchOptions): Promise<ListPage<BatchEntry>> {
    return batchEntries("/_backlit/userdata-batch", prefix, opts, false);
  },

  async captureCreate(key: string, body: BodyInit, contentType: string): Promise<CaptureCreateResult> {
    const res = await putJSON(
      "POST",
      `/_backlit/capture?prefix=${encodeURIComponent(key)}`,
      body,
      contentType,
    );
    if (typeof res.handle !== "string" || res.handle.length === 0) {
      throw new BacklitError(
        "internal",
        "server response missing handle",
        201,
      );
    }
    if (typeof res.crc32c !== "string" || res.crc32c.length === 0) {
      throw missingCRC32C();
    }
    return { handle: res.handle, crc32c: res.crc32c };
  },

  async captureUpdate(handle: string, body: BodyInit, contentType: string): Promise<PutResult> {
    const res = await putJSON(
      "PUT",
      `/_backlit/capture/${encodeURIComponent(handle)}`,
      body,
      contentType,
    );
    if (typeof res.crc32c !== "string" || res.crc32c.length === 0) {
      throw missingCRC32C();
    }
    return { crc32c: res.crc32c };
  },

  captureGet(handle: string): Promise<BlobResult> {
    return request({
      method: "GET",
      path: `/_backlit/capture/${encodeURIComponent(handle)}`,
      expect: "blob",
    });
  },

  async captureList(prefix: string, opts?: ListOptions): Promise<ListPage<CaptureListEntry>> {
    // Reuse the data/userdata list wire shape
    // ({keys:[{key,crc32c,content_type,size}],next}), surfacing each row's
    // `key` as the capture `handle` and forwarding contentType / size + next.
    const page = await listKeys("/_backlit/capture", prefix, opts);
    return {
      items: page.items.map((e) => ({
        handle: e.key,
        crc32c: e.crc32c,
        contentType: e.contentType,
        size: e.size,
      })),
      next: page.next,
    };
  },

  captureDelete(handle: string): Promise<void> {
    return request({
      method: "DELETE",
      path: `/_backlit/capture/${encodeURIComponent(handle)}`,
      expect: "void",
    });
  },

  recordsGet(key: string): Promise<RecordResult> {
    return recordsGetRequest(`/_backlit/records/${encodeURIComponent(key)}`);
  },

  async recordsPut(key: string, body: BodyInit, contentType: string): Promise<PutResult> {
    // Plain put returns just the crc — the writer is the owner (the server
    // still sets the owner header on the 200; it rides only the result's meta).
    const res = await putJSON("PUT", `/_backlit/records/${encodeURIComponent(key)}`, body, contentType);
    if (typeof res.crc32c !== "string" || res.crc32c.length === 0) {
      throw missingCRC32C();
    }
    return tagMeta({ crc32c: res.crc32c }, responseMetaOf(res));
  },

  recordsPutIfMatch(
    key: string,
    body: BodyInit,
    contentType: string,
    expectedCrc32c: string,
  ): Promise<RecordPutIfMatchResult> {
    return recordsRequestPutIfMatch(
      `/_backlit/records-cas/${encodeURIComponent(key)}`,
      body,
      contentType,
      expectedCrc32c,
    );
  },

  recordsDelete(key: string): Promise<void> {
    return deleteWithMeta("records", key).then(() => undefined);
  },

  recordsList(prefix: string, opts?: ListOptions): Promise<ListPage<RecordListEntry>> {
    return listRecordKeys("/_backlit/records", prefix, opts);
  },

  recordsGetBatch(prefix: string, opts?: GetBatchOptions): Promise<ListPage<RecordBatchEntry>> {
    return batchEntries("/_backlit/records-batch", prefix, opts, true) as Promise<ListPage<RecordBatchEntry>>;
  },

  recordsReassign(key: string, newOwnerUid: string): Promise<void> {
    // newOwnerUid rides the X-Backlit-New-Owner header (the Slice 3 wire
    // contract); a 204 resolves void.
    return request({
      method: "POST",
      path: `/_backlit/records-reassign/${encodeURIComponent(key)}`,
      headers: { "X-Backlit-New-Owner": newOwnerUid },
      expect: "void",
    });
  },

  onStorageChange(handler: StorageChangeHandler): Unsubscribe {
    return remoteOnStorageChange(handler);
  },

  async pushSubscribe(payload: PushSubscribePayload): Promise<SubscribeResult> {
    const res = await request<{ endpointId?: unknown; reregisterToken?: unknown }>({
      method: "POST",
      path: "/_backlit/push/subscribe",
      body: JSON.stringify(payload),
      contentType: "application/json",
      expect: "json",
    });
    return {
      endpointId: typeof res.endpointId === "string" ? res.endpointId : "",
      // Empty on an idempotent re-subscribe of an already-known endpoint —
      // tolerate-missing to "" (the SW keeps its prior token in that case).
      reregisterToken: typeof res.reregisterToken === "string" ? res.reregisterToken : "",
    };
  },

  pushUnsubscribe(payload: PushUnsubscribePayload): Promise<void> {
    return request({
      method: "POST",
      path: "/_backlit/push/unsubscribe",
      body: JSON.stringify(payload),
      contentType: "application/json",
      expect: "void",
    });
  },

  pushTest(): Promise<void> {
    // 202 { queued: true } on success; the endpoint returns 404 not_found when
    // the signed-in user has no active subscription (surfaced via decodeError).
    return request({
      method: "POST",
      path: "/_backlit/push/test",
      expect: "void",
    });
  },
};

// The /_backlit/ui answer is memoized (the last successful decode, with its
// ResponseMeta attached) and announced to listeners, so offline.enable() —
// which usually runs after the FAB fetched it at load — still sees it without a
// second request (RM-1).
let uiMemo: UIConfig | null = null;
let uiInflight: Promise<UIConfig> | null = null;
const uiListeners = new Set<(cfg: UIConfig) => void>();

// getUIConfigMemo returns the last successful /_backlit/ui answer, or null.
export function getUIConfigMemo(): UIConfig | null {
  return uiMemo;
}

// onUIConfig registers fn for every later successful /_backlit/ui answer;
// returns an unregister.
export function onUIConfig(fn: (cfg: UIConfig) => void): () => void {
  uiListeners.add(fn);
  return () => {
    uiListeners.delete(fn);
  };
}

// _resetTransportForTest clears the /ui memo + in-flight request, the /ui
// listeners and the response + failure observers.
export function _resetTransportForTest(): void {
  uiMemo = null;
  uiInflight = null;
  uiListeners.clear();
  observers.clear();
  failureObservers.clear();
}

// UIConfigResponse is the /_backlit/ui JSON shape. Every field tolerated-missing
// so a partial/older server body decodes to safe defaults.
interface UIConfigResponse {
  user?: AuthMeResponse | null;
  auth_mode?: string | null;
  login_available?: unknown;
  can_hide_branding?: unknown;
  branding_hidden?: unknown;
  marketing_url?: string | null;
  title?: string | null;
  // Push config (Slice 5): a nested { enabled, vapid_public_key, reregister_url }
  // object. Tolerated-missing (absent → push disabled) like every other field.
  push?: {
    enabled?: unknown;
    vapid_public_key?: string | null;
    reregister_url?: string | null;
  } | null;
}

// decodeUIConfig maps the /_backlit/ui body into a UIConfig. user is decoded
// with the same per-field tolerance authMe uses (null unless an identity anchor
// is present); booleans are strict `=== true` (missing → false); marketing_url
// is validated to an https: URL else falls back to the canonical default so a
// bad value can never become a javascript:/data: href on the FAB link.
function decodeUIConfig(res: UIConfigResponse | null): UIConfig {
  const obj = res && typeof res === "object" ? res : {};
  let user: BacklitUser | null = null;
  const u = obj.user;
  if (u && typeof u === "object") {
    const email = typeof u.email === "string" ? u.email : "";
    const uid = typeof u.uid === "string" ? u.uid : "";
    if (email !== "" || uid !== "") {
      const permission = PERMISSIONS.has(u.permission as Permission)
        ? (u.permission as Permission)
        : "";
      user = {
        uid,
        email,
        name: typeof u.name === "string" ? u.name : "",
        mobile: typeof u.mobile === "string" ? u.mobile : "",
        permission,
      };
    }
  }
  const push = obj.push && typeof obj.push === "object" ? obj.push : {};
  return {
    user,
    authMode: typeof obj.auth_mode === "string" ? obj.auth_mode : "",
    loginAvailable: obj.login_available === true,
    canHideBranding: obj.can_hide_branding === true,
    brandingHidden: obj.branding_hidden === true,
    marketingUrl: safeMarketingURL(obj.marketing_url),
    title: typeof obj.title === "string" ? obj.title : "",
    pushEnabled: push.enabled === true,
    vapidPublicKey: typeof push.vapid_public_key === "string" ? push.vapid_public_key : "",
    pushReregisterUrl: typeof push.reregister_url === "string" ? push.reregister_url : "",
  };
}

// safeMarketingURL accepts only an https: absolute URL; anything else (missing,
// non-string, http:, javascript:, a relative value) falls back to the canonical
// marketing site so the FAB's "Get your own glow" link can never be hijacked.
function safeMarketingURL(raw: unknown): string {
  const fallback = "https://backlit.run";
  if (typeof raw !== "string" || raw === "") return fallback;
  try {
    const u = new URL(raw);
    return u.protocol === "https:" ? raw : fallback;
  } catch {
    return fallback;
  }
}

// decodeRoster maps a /_backlit/auth/users {users:[...], next} envelope into a
// ListPage<BacklitUser>. It mirrors authMe's per-field tolerance: each field
// decodes `typeof x === "string" ? x : ""`, and an unrecognized permission
// collapses to "" (PERMISSIONS.has). A missing/non-array `users` (or a
// non-object entry) tolerates to { items: [], next: "" } / skips the row
// rather than throwing — the privileged-throw is for transport/auth failures
// (handled upstream in request()), not for a malformed-but-200 body. The
// `next` cursor uses the same tolerate-missing policy as the list surfaces
// (decodeNext → "" when absent/exhausted).
function decodeRoster(res: AuthUsersResponse): ListPage<BacklitUser> {
  const out: BacklitUser[] = [];
  if (Array.isArray(res.users)) {
    for (const entry of res.users) {
      if (entry === null || typeof entry !== "object") continue;
      const permission = PERMISSIONS.has(entry.permission as Permission)
        ? (entry.permission as Permission)
        : "";
      out.push({
        uid: typeof entry.uid === "string" ? entry.uid : "",
        name: typeof entry.name === "string" ? entry.name : "",
        email: typeof entry.email === "string" ? entry.email : "",
        mobile: typeof entry.mobile === "string" ? entry.mobile : "",
        permission,
      });
    }
  }
  return { items: out, next: decodeNext(res.next) };
}

// ListResponse mirrors the data-api JSON shape: each entry is
// {key, crc32c, content_type, size} and the envelope carries a `next` opaque
// cursor (DATA-1). The SDK exposes those as ListEntry to app code
// (content_type → contentType, size → size) inside a ListPage<…>.
interface ListResponse {
  keys?: Array<{ key?: unknown; crc32c?: unknown; content_type?: unknown; size?: unknown }>;
  next?: unknown;
}

// listQuery builds the ?prefix=&cursor=&limit= query string the list
// endpoints accept. Empty prefix / absent cursor / non-finite limit are
// omitted; the server applies its own clamp + default, so the SDK forwards
// the values verbatim rather than validating them.
function listQuery(prefix: string, opts?: ListOptions): string {
  const params = new URLSearchParams();
  if (prefix.length > 0) params.set("prefix", prefix);
  if (opts?.cursor) params.set("cursor", opts.cursor);
  if (typeof opts?.limit === "number" && Number.isFinite(opts.limit)) {
    params.set("limit", String(Math.trunc(opts.limit)));
  }
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : "";
}

// pageQuery builds the ?cursor=&limit= query for prefix-less paginated
// surfaces (auth.users()). Mirrors listQuery minus the prefix leg.
function pageQuery(opts?: ListOptions): string {
  const params = new URLSearchParams();
  if (opts?.cursor) params.set("cursor", opts.cursor);
  if (typeof opts?.limit === "number" && Number.isFinite(opts.limit)) {
    params.set("limit", String(Math.trunc(opts.limit)));
  }
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : "";
}

// decodeNext reads the opaque `next` cursor from a list envelope, tolerating a
// missing/non-string value as "" (exhausted) — the same tolerate-missing policy
// the per-entry fields use.
function decodeNext(next: unknown): string {
  return typeof next === "string" ? next : "";
}

async function listKeys(basePath: string, prefix: string, opts?: ListOptions): Promise<ListPage<ListEntry>> {
  const res = await request<ListResponse>({
    method: "GET",
    path: `${basePath}${listQuery(prefix, opts)}`,
    expect: "json",
  });
  const items: ListEntry[] = [];
  if (Array.isArray(res.keys)) {
    for (const entry of res.keys) {
      if (entry === null || typeof entry !== "object") continue;
      const k = entry.key;
      const c = entry.crc32c;
      const ct = entry.content_type;
      const n = entry.size;
      if (typeof k !== "string" || k.length === 0) continue;
      // FE8 — same read policy as GET (above): a per-entry crc32c that's
      // missing or non-string TOLERATES to "" rather than dropping the row or
      // throwing. The key is the payload of a LIST; the crc is an optional
      // dedup aid. content_type / size follow the same tolerate-missing policy
      // (→ "" / 0).
      items.push({
        key: k,
        crc32c: typeof c === "string" ? c : "",
        contentType: typeof ct === "string" ? ct : "",
        size: typeof n === "number" ? n : 0,
      });
    }
  }
  return tagMeta({ items, next: decodeNext(res.next) }, responseMetaOf(res));
}

// RecordListResponse is the records LIST wire shape: like ListResponse but each
// entry also carries the owner uid.
interface RecordListResponse {
  keys?: Array<{ key?: unknown; crc32c?: unknown; content_type?: unknown; size?: unknown; owner?: unknown }>;
  next?: unknown;
}

// listRecordKeys is the records LIST decoder — listKeys plus a per-entry owner
// (tolerate-missing → "", the FE8 policy, matching crc).
async function listRecordKeys(basePath: string, prefix: string, opts?: ListOptions): Promise<ListPage<RecordListEntry>> {
  const res = await request<RecordListResponse>({
    method: "GET",
    path: `${basePath}${listQuery(prefix, opts)}`,
    expect: "json",
  });
  const items: RecordListEntry[] = [];
  if (Array.isArray(res.keys)) {
    for (const entry of res.keys) {
      if (entry === null || typeof entry !== "object") continue;
      const k = entry.key;
      const c = entry.crc32c;
      const ct = entry.content_type;
      const n = entry.size;
      const o = entry.owner;
      if (typeof k !== "string" || k.length === 0) continue;
      items.push({
        key: k,
        crc32c: typeof c === "string" ? c : "",
        contentType: typeof ct === "string" ? ct : "",
        size: typeof n === "number" ? n : 0,
        owner: typeof o === "string" ? o : "",
      });
    }
  }
  return tagMeta({ items, next: decodeNext(res.next) }, responseMetaOf(res));
}

// --- batch surface --------------------------------------------------------
// getBatch is the value-bearing sibling of list: the same list envelope
// ({keys:[...],next}) but each entry also carries `value` (standard padded
// base64 — Go's []byte marshal), `omitted` (bool, only ever true), an optional
// `reason` string (open vocabulary; v1 wire value "too_large"), and — on the
// records surface — `owner`.

// BatchResponse is the getBatch wire shape: the ListResponse row fields plus the
// value-bearing extras. Every extra is `unknown` so the decoder validates each
// per-entry field itself (the same tolerate-missing discipline as ListResponse).
interface BatchResponse {
  keys?: Array<{
    key?: unknown;
    crc32c?: unknown;
    content_type?: unknown;
    size?: unknown;
    value?: unknown;
    omitted?: unknown;
    reason?: unknown;
    owner?: unknown;
  }>;
  next?: unknown;
}

// decodeStdBase64 decodes a STANDARD RFC 4648 (with padding) base64 string —
// what Go's encoding/json emits for a []byte — into raw bytes. (Contrast
// urlBase64ToUint8Array in push.ts, which is base64url + VAPID-specific.) Reuses
// the same atob + charCodeAt loop; throws on malformed input so the caller can
// degrade that one row to the omitted arm rather than fail the page.
function decodeStdBase64(b64: string): Uint8Array {
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// batchEntries is the shared getBatch decoder for data/userdata (withOwner
// false) and records (withOwner true). It implements the pinned decode rule +
// the FE8 tolerances:
//   - key-less / non-object row → dropped;
//   - crc/content_type/size tolerate-missing → "" / "" / 0;
//   - `typeof value === "string"` ⇒ INCLUDED — decode the std-base64 into a
//     Blob typed with the entry's content_type ("" when absent, list-style, NOT
//     the GET octet-stream default). "" is a real zero-byte Blob, not omission.
//     Malformed base64 degrades THAT row to the omitted arm (no invented
//     reason) — never fails the page;
//   - else `omitted === true` ⇒ metadata-only arm (carry `reason` iff a string);
//   - else (neither) ⇒ dropped defensively;
//   - `next` via decodeNext (tolerate-missing → "").
// When withOwner, each row also carries owner (tolerate-missing → "").
async function batchEntries(
  basePath: string,
  prefix: string,
  opts: GetBatchOptions | undefined,
  withOwner: boolean,
): Promise<ListPage<BatchEntry>> {
  const res = await request<BatchResponse>({
    method: "GET",
    path: `${basePath}${listQuery(prefix, opts)}`,
    expect: "json",
  });
  const items: BatchEntry[] = [];
  if (Array.isArray(res.keys)) {
    for (const entry of res.keys) {
      if (entry === null || typeof entry !== "object") continue;
      const k = entry.key;
      if (typeof k !== "string" || k.length === 0) continue;
      const c = entry.crc32c;
      const ct = entry.content_type;
      const n = entry.size;
      const meta = {
        key: k,
        crc32c: typeof c === "string" ? c : "",
        contentType: typeof ct === "string" ? ct : "",
        size: typeof n === "number" ? n : 0,
        ...(withOwner ? { owner: typeof entry.owner === "string" ? entry.owner : "" } : {}),
      };

      if (typeof entry.value === "string") {
        // INCLUDED. A malformed base64 payload degrades this one row to the
        // omitted arm rather than failing the whole page.
        try {
          const bytes = decodeStdBase64(entry.value);
          // Pass the backing ArrayBuffer (not the Uint8Array view) so the Blob
          // ctor's BlobPart type is satisfied across TS lib versions.
          const value = new Blob([bytes.buffer as ArrayBuffer], { type: meta.contentType });
          items.push({ ...meta, value } as BatchEntry);
        } catch {
          items.push({ ...meta, omitted: true } as BatchEntry);
        }
        continue;
      }
      if (entry.omitted === true) {
        // Metadata-only arm; carry `reason` when the server sent a string.
        const reason = typeof entry.reason === "string" ? entry.reason : undefined;
        items.push({ ...meta, omitted: true, ...(reason !== undefined ? { reason } : {}) } as BatchEntry);
        continue;
      }
      // Neither an included value nor an explicit omission → drop defensively.
    }
  }
  return tagMeta({ items, next: decodeNext(res.next) }, responseMetaOf(res));
}
